import { useEffect, useMemo, useRef, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'
import { debounce } from 'lodash-es'
import {
  AccountType,
  BankAccount,
  UpdateBankAccountInput,
  useCreateBankAccountMutation,
  useUpdateBankAccountMutation,
  useUpdateMerchantApplicationMutation,
  useSubmitMerchantApplicationMutation,
  useCreateEntityMccMutation,
  useUpdateEntityArchiveMccMutation,
  CountryAlpha2
} from 'generated/graphql'
import { DEBOUNCE_MS_VALUE, DEFAULT_MCC_CARD_SCHEMAS } from 'consts'
import { mapFormToMerchantApplication } from 'utils'
import { mapMerchantApplicationToForm } from 'utils/map-merchant-application-to-form'
import { useCloudSaveState } from 'components/CloudSaveIndicator/useCloudSaveState'
import {
  EntityMccs,
  Individuals,
  MerchantApplicationData,
  StepValidationStatus,
  TemporaryScreen
} from 'pages/Onboarding/types'
import { useOnboardingRegion } from './use-onboarding-region'

type UseOnboardingFormParams = {
  merchantApplication: MerchantApplicationData
  bankAccounts?: BankAccount[]
  individuals: Individuals
  entityMccs?: EntityMccs | null
  setTemporaryScreen: (value: TemporaryScreen | null) => void
}

export const useOnboardingForm = ({
  merchantApplication,
  bankAccounts,
  individuals,
  entityMccs,
  setTemporaryScreen
}: UseOnboardingFormParams) => {
  const { t } = useTranslation()
  const [updateMerchantApplication] = useUpdateMerchantApplicationMutation()
  const [updateBankAccount] = useUpdateBankAccountMutation()
  const [submitMerchantApplication] = useSubmitMerchantApplicationMutation({
    refetchQueries: ['getMerchantApplication']
  })
  const [createEntityMcc] = useCreateEntityMccMutation({
    refetchQueries: ['getMerchantApplication'],
    awaitRefetchQueries: true
  })
  const [archiveEntityMcc] = useUpdateEntityArchiveMccMutation({
    refetchQueries: ['getMerchantApplication'],
    awaitRefetchQueries: true
  })

  const [createBankAccount] = useCreateBankAccountMutation({
    refetchQueries: ['getMerchantApplication']
  })

  const { cloudState, onSaveStart, onSaveSuccess, onSaveError } =
    useCloudSaveState()

  const stepStorageKey = `onboarding-current-step:${merchantApplication.merchantId}`
  const dirtyStepsStorageKey = `onboarding-dirty-steps:${merchantApplication.merchantId}`

  const [currentStepIndex, _setCurrentStepIndex] = useState(() => {
    const saved = localStorage.getItem(stepStorageKey)
    if (saved == null) return 0
    const parsed = Number(saved)
    if (Number.isNaN(parsed) || parsed < 0) return 0
    return parsed
  })

  const setCurrentStepIndex = (index: number) => {
    localStorage.setItem(stepStorageKey, String(index))
    _setCurrentStepIndex(index)
  }

  const [stepStatuses, setStepStatuses] = useState<
    Record<string, StepValidationStatus>
  >({})

  const [dirtySteps, _setDirtySteps] = useState<Set<string>>(() => {
    const saved = localStorage.getItem(dirtyStepsStorageKey)
    if (!saved) return new Set()
    try {
      return new Set(JSON.parse(saved))
    } catch {
      return new Set()
    }
  })

  const setDirtySteps = (updater: (prev: Set<string>) => Set<string>) => {
    _setDirtySteps(prev => {
      const next = updater(prev)
      localStorage.setItem(dirtyStepsStorageKey, JSON.stringify([...next]))
      return next
    })
  }

  const latestEntityMccs = useRef(entityMccs)
  latestEntityMccs.current = entityMccs

  const steps = useOnboardingRegion({ merchantApplication })
  const clampedIndex = Math.max(0, Math.min(currentStepIndex, steps.length - 1))
  const currentStep = steps[clampedIndex]
  const isLastStep = clampedIndex === steps.length - 1
  const nextStep = steps[clampedIndex + 1]

  const mappedDefaults = useMemo(
    () =>
      mapMerchantApplicationToForm(
        merchantApplication,
        bankAccounts,
        individuals,
        entityMccs
      ),
    [merchantApplication, bankAccounts, individuals, entityMccs]
  )

  const methods = useForm({
    defaultValues: mappedDefaults,
    resolver: currentStep.formConfig
      ? zodResolver(currentStep.formConfig.schema)
      : undefined,
    mode: 'onBlur',
    reValidateMode: 'onChange'
  })

  const { isSubmitting } = methods.formState

  useEffect(() => {
    if (dirtySteps.size === 0) return

    const values = methods.getValues()
    const statuses: Record<string, StepValidationStatus> = {}

    steps.forEach(step => {
      if (!step.formConfig?.schema || !dirtySteps.has(step.id)) return
      const result = step.formConfig.schema.safeParse(values)
      statuses[step.id] = result.success ? 'valid' : 'error'
    })

    setStepStatuses(statuses)
  }, [])

  const handleChange = useMemo(
    () =>
      debounce(async (data: FieldValues) => {
        if (!currentStep.formConfig?.schema) return

        onSaveStart()

        try {
          const accounts = data.bankAccounts ?? []
          const serverMccs = latestEntityMccs.current ?? []
          const formMccs = (data.mccCodes ?? []) as EntityMccs
          const activeCodes = new Set(
            formMccs.filter(mcc => !mcc.archived).map(mcc => mcc.code)
          )

          const mccsToCreate = [...activeCodes].filter(
            code => !serverMccs.some(mcc => mcc.code === code)
          )
          const mccsToToggle = serverMccs.filter(
            mcc => mcc.archived === activeCodes.has(mcc.code)
          )

          await Promise.all([
            updateMerchantApplication({
              variables: {
                input: {
                  ...mapFormToMerchantApplication(data),
                  stage: clampedIndex
                }
              }
            }),
            ...accounts
              .filter((account: UpdateBankAccountInput) => account?.id)
              .map((account: UpdateBankAccountInput) =>
                updateBankAccount({
                  variables: {
                    input: {
                      ...account,
                      currency: 'USD',
                      bankCountry: CountryAlpha2.Us
                    }
                  }
                })
              ),
            ...mccsToCreate.map(code =>
              createEntityMcc({
                variables: {
                  input: { code, cardSchemas: DEFAULT_MCC_CARD_SCHEMAS }
                }
              })
            ),
            ...mccsToToggle.map(mcc =>
              archiveEntityMcc({
                variables: { input: { id: mcc.id, archived: !mcc.archived } }
              })
            )
          ])

          onSaveSuccess()
        } catch {
          onSaveError()
        }
      }, DEBOUNCE_MS_VALUE),
    [currentStep.id]
  )

  useEffect(() => {
    methods.clearErrors()

    if (stepStatuses[currentStep.id] === 'error') {
      setTimeout(() => methods.trigger(), 0)
    }

    const subscription = methods.watch((_, { name }) => {
      setDirtySteps(prev => {
        if (prev.has(currentStep.id)) return prev
        return new Set(prev).add(currentStep.id)
      })
      handleChange(methods.getValues())

      if (name) {
        methods.trigger(name)
      }
    })

    return () => {
      subscription.unsubscribe()
      handleChange.cancel()
    }
  }, [currentStep.id])

  const validateAndChangeStep = (nextIndex: number) => {
    const schema = currentStep.formConfig?.schema

    if (schema && dirtySteps.has(currentStep.id)) {
      const result = schema.safeParse(methods.getValues())
      setStepStatuses(prev => ({
        ...prev,
        [currentStep.id]: result.success ? 'valid' : 'error'
      }))
    }

    setCurrentStepIndex(nextIndex)
  }

  const validateAllSteps = (currentStepStatus: StepValidationStatus) => {
    const values = methods.getValues()

    const newStatuses: Record<string, StepValidationStatus> = {
      ...stepStatuses,
      [currentStep.id]: currentStepStatus
    }

    let firstErrorIndex: number | null = null

    steps.forEach((step, index) => {
      if (!step.formConfig?.schema || index === clampedIndex) return

      const result = step.formConfig.schema.safeParse(values)
      const status = result.success ? 'valid' : 'error'
      newStatuses[step.id] = status

      if (status === 'error' && firstErrorIndex === null) {
        firstErrorIndex = index
      }
    })

    setStepStatuses(newStatuses)
    return firstErrorIndex
  }

  const handleFormSubmit = methods.handleSubmit(
    async () => {
      const firstErrorIndex = validateAllSteps('valid')

      if (firstErrorIndex !== null) {
        setCurrentStepIndex(firstErrorIndex)
        return
      }

      await submitMerchantApplication()
      setTemporaryScreen('onboarding-submitted')
      localStorage.removeItem(stepStorageKey)
      localStorage.removeItem(dirtyStepsStorageKey)
    },
    () => {
      validateAllSteps('error')
    }
  )

  const handleContinue = () => {
    validateAndChangeStep(clampedIndex + 1)
  }

  const bankAccountCreated = useRef(!!bankAccounts?.length)

  useEffect(() => {
    if (
      currentStep.id === 'banking-information' &&
      !bankAccountCreated.current
    ) {
      bankAccountCreated.current = true
      createBankAccount({
        variables: {
          input: {
            currency: 'USD',
            bankCountry: CountryAlpha2.Us
          }
        }
      })
    }
  }, [currentStep.id])

  useEffect(() => {
    if (bankAccounts?.length) {
      methods.setValue(
        'bankAccounts',
        bankAccounts.map(account => ({
          ...account,
          accountType: account.accountType ?? AccountType.Checking
        }))
      )
    }
  }, [bankAccounts])

  const submitText = isLastStep
    ? t('actions.submit')
    : t('actions.continueTo', { step: nextStep?.title })

  return {
    methods,
    steps,
    currentStep,
    clampedIndex,
    isLastStep,
    isSubmitting,
    cloudState,
    stepStatuses,
    submitText,
    validateAndChangeStep,
    handleFormSubmit,
    handleContinue
  }
}
